import { useRef, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { anyApi } from "convex/server";
import { Dialog } from "./Dialog";
import { Field, inputCls } from "./form-controls";

interface Unit {
  _id: string;
  label: string;
  bedrooms: number | null;
  bathrooms: number | null;
  notes: string | null;
  thumbnailUrl: string | null;
}

// Landlord-side units manager — list, add, inline-edit, delete units
// on a property. Per-unit thumbnail upload / replace / remove lives
// in the row.
export function UnitsManagerDialog({
  open,
  onClose,
  propertySlug,
}: {
  open: boolean;
  onClose: () => void;
  propertySlug: string;
}) {
  const units = useQuery(
    anyApi.units.listForProperty,
    open ? { propertySlug } : "skip",
  ) as Unit[] | undefined;

  return (
    <Dialog open={open} onClose={onClose} title="Units" width="lg">
      <div className="px-6 py-5 space-y-5">
        {units === undefined ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : units.length === 0 ? (
          <p className="text-sm text-slate-500">No units yet — add one below.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {units.map((u) => (
              <UnitRow key={u._id} unit={u} />
            ))}
          </ul>
        )}
        <AddUnitForm propertySlug={propertySlug} />
      </div>
    </Dialog>
  );
}

function UnitRow({ unit }: { unit: Unit }) {
  const generate = useMutation(anyApi.units.generateThumbnailUploadUrl);
  const setThumbnail = useMutation(anyApi.units.setThumbnail);
  const removeThumbnail = useMutation(anyApi.units.removeThumbnail);
  const update = useMutation(anyApi.units.update);
  const remove = useMutation(anyApi.units.remove);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(unit.label);
  const [bedrooms, setBedrooms] = useState(
    unit.bedrooms === null ? "" : String(unit.bedrooms),
  );
  const [bathrooms, setBathrooms] = useState(
    unit.bathrooms === null ? "" : String(unit.bathrooms),
  );
  const [notes, setNotes] = useState(unit.notes ?? "");

  const startEdit = () => {
    setLabel(unit.label);
    setBedrooms(unit.bedrooms === null ? "" : String(unit.bedrooms));
    setBathrooms(unit.bathrooms === null ? "" : String(unit.bathrooms));
    setNotes(unit.notes ?? "");
    setError(null);
    setEditing(true);
  };

  const handleFile = async (file: File | null) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const uploadUrl = (await generate({ unitId: unit._id })) as string;
      const res = await fetch(uploadUrl, {
        method: "POST",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
      });
      if (!res.ok) {
        throw new Error(`upload failed: ${res.status} ${res.statusText}`);
      }
      const { storageId } = (await res.json()) as { storageId: string };
      await setThumbnail({ unitId: unit._id, storageId });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRemoveThumbnail = async () => {
    if (!confirm(`Remove the thumbnail for ${unit.label}?`)) return;
    setBusy(true);
    setError(null);
    try {
      await removeThumbnail({ unitId: unit._id });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleSaveEdit = async () => {
    setBusy(true);
    setError(null);
    try {
      const bd = bedrooms.trim() === "" ? undefined : Number(bedrooms);
      const ba = bathrooms.trim() === "" ? undefined : Number(bathrooms);
      if (bd !== undefined && !Number.isFinite(bd)) {
        throw new Error("bedrooms must be a number");
      }
      if (ba !== undefined && !Number.isFinite(ba)) {
        throw new Error("bathrooms must be a number");
      }
      await update({
        unitId: unit._id,
        label,
        bedrooms: bd,
        bathrooms: ba,
        notes,
      });
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (
      !confirm(
        `Delete unit ${unit.label}?\n\nOnly works if no leases or tenancies still reference it.`,
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await remove({ unitId: unit._id });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="py-4 first:pt-0">
      <div className="flex items-start gap-4">
        <UnitThumbnail url={unit.thumbnailUrl} label={unit.label} size="md" />
        <div className="flex-1 min-w-0">
          {editing ? (
            <div className="space-y-2">
              <Field label="Label" required>
                <input
                  className={inputCls}
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                />
              </Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Bedrooms">
                  <input
                    className={inputCls}
                    inputMode="numeric"
                    value={bedrooms}
                    onChange={(e) => setBedrooms(e.target.value)}
                  />
                </Field>
                <Field label="Bathrooms">
                  <input
                    className={inputCls}
                    inputMode="decimal"
                    value={bathrooms}
                    onChange={(e) => setBathrooms(e.target.value)}
                  />
                </Field>
              </div>
              <Field label="Notes">
                <textarea
                  className={`${inputCls} min-h-[64px]`}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </Field>
            </div>
          ) : (
            <>
              <p className="text-sm font-medium text-slate-900">{unit.label}</p>
              <p className="text-xs text-slate-500">
                {[
                  unit.bedrooms !== null && `${unit.bedrooms} bd`,
                  unit.bathrooms !== null && `${unit.bathrooms} ba`,
                  unit.thumbnailUrl ? "thumbnail set" : "no thumbnail",
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
              {unit.notes && (
                <p className="mt-1 text-xs text-slate-600 whitespace-pre-wrap">
                  {unit.notes}
                </p>
              )}
            </>
          )}
          {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
        </div>
        <div className="shrink-0 flex flex-col items-end gap-1.5">
          {editing ? (
            <>
              <button
                type="button"
                onClick={handleSaveEdit}
                disabled={busy}
                className="rounded-md bg-slate-900 px-2.5 py-1 text-[11px] font-medium text-white hover:bg-slate-800 transition-colors disabled:opacity-50"
              >
                {busy ? "Saving…" : "Save"}
              </button>
              <button
                type="button"
                onClick={() => setEditing(false)}
                disabled={busy}
                className="text-[11px] font-medium text-slate-500 hover:text-slate-900 px-2 py-1 rounded hover:bg-slate-100 disabled:opacity-50"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <div className="flex items-center gap-1.5">
                <label className="cursor-pointer">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
                    className="sr-only"
                    disabled={busy}
                  />
                  <span className="inline-block rounded-md border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50 transition-colors">
                    {busy ? "…" : unit.thumbnailUrl ? "Replace" : "Upload"}
                  </span>
                </label>
                {unit.thumbnailUrl && (
                  <button
                    type="button"
                    onClick={handleRemoveThumbnail}
                    disabled={busy}
                    className="text-[11px] font-medium text-slate-500 hover:text-red-600 transition-colors px-2 py-1 rounded hover:bg-slate-100 disabled:opacity-50"
                  >
                    Remove
                  </button>
                )}
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={startEdit}
                  disabled={busy}
                  className="text-[11px] font-medium text-slate-700 hover:text-slate-900 px-2 py-1 rounded hover:bg-slate-100 disabled:opacity-50"
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={busy}
                  className="text-[11px] font-medium text-slate-500 hover:text-red-600 px-2 py-1 rounded hover:bg-slate-100 disabled:opacity-50"
                >
                  Delete
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </li>
  );
}

function AddUnitForm({ propertySlug }: { propertySlug: string }) {
  const create = useMutation(anyApi.units.create);
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [bedrooms, setBedrooms] = useState("");
  const [bathrooms, setBathrooms] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setLabel("");
    setBedrooms("");
    setBathrooms("");
    setNotes("");
    setError(null);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const bd = bedrooms.trim() === "" ? undefined : Number(bedrooms);
      const ba = bathrooms.trim() === "" ? undefined : Number(bathrooms);
      if (bd !== undefined && !Number.isFinite(bd)) {
        throw new Error("bedrooms must be a number");
      }
      if (ba !== undefined && !Number.isFinite(ba)) {
        throw new Error("bathrooms must be a number");
      }
      await create({
        propertySlug,
        label,
        bedrooms: bd,
        bathrooms: ba,
        notes: notes.trim() || undefined,
      });
      reset();
      setOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="rounded-md border border-dashed border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 transition-colors"
      >
        + Add a unit
      </button>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3"
    >
      <p className="text-xs font-medium uppercase tracking-[0.18em] text-slate-500">
        New unit
      </p>
      <Field label="Label" required>
        <input
          className={inputCls}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="e.g., Garage"
          autoFocus
        />
      </Field>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Bedrooms">
          <input
            className={inputCls}
            inputMode="numeric"
            value={bedrooms}
            onChange={(e) => setBedrooms(e.target.value)}
          />
        </Field>
        <Field label="Bathrooms">
          <input
            className={inputCls}
            inputMode="decimal"
            value={bathrooms}
            onChange={(e) => setBathrooms(e.target.value)}
          />
        </Field>
      </div>
      <Field label="Notes">
        <textarea
          className={`${inputCls} min-h-[64px]`}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </Field>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            reset();
          }}
          disabled={busy}
          className="text-xs font-medium text-slate-600 hover:text-slate-900 px-3 py-1.5 rounded hover:bg-slate-100 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={busy}
          className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 transition-colors disabled:opacity-50"
        >
          {busy ? "Adding…" : "Add unit"}
        </button>
      </div>
    </form>
  );
}

// Reusable across the app — Tenants lease card uses size="sm",
// Units manager uses size="md", future unit pickers can use "xs".
export function UnitThumbnail({
  url,
  label,
  size = "sm",
}: {
  url: string | null;
  label: string;
  size?: "xs" | "sm" | "md";
}) {
  const dim = size === "xs" ? "h-6 w-6" : size === "sm" ? "h-9 w-9" : "h-14 w-14";
  const text =
    size === "xs"
      ? "text-[8px]"
      : size === "sm"
        ? "text-[10px]"
        : "text-xs";
  if (url) {
    return (
      <img
        src={url}
        alt={label}
        className={`${dim} rounded-md object-cover bg-slate-100 ring-1 ring-slate-200`}
      />
    );
  }
  return (
    <div
      aria-label={label}
      className={`${dim} ${text} rounded-md bg-slate-100 ring-1 ring-slate-200 flex items-center justify-center font-medium uppercase text-slate-500 tracking-wider`}
    >
      {initials(label)}
    </div>
  );
}

function initials(label: string): string {
  const parts = label.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2);
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
